// Roller og rettigheder. Hver kontorrolle har et sæt "evner". Ejer og admin kan rette, hvad
// kundeservice må (under Brugere og roller). Nogle ting kan kun ejeren, og det kan ikke ændres.
import type { Rolle } from './typer.ts';

export type Evne =
  | 'bookinger.se' | 'bookinger.ret' | 'bookinger.godkend'
  | 'kunder.se' | 'kunder.ret' | 'kunder.slet'
  | 'kundecenter.se' | 'kundecenter.behandl' | 'kundecenter.arkiv' | 'beskeder.send'
  | 'chatbot.se' | 'chatbot.ret'
  | 'betalinger.se' | 'betalinger.ret' | 'pris.ret'
  | 'medlemskaber.se' | 'medlemskaber.ret'
  | 'katalog.ret' | 'steamere.ret' | 'fravaer.meld'
  | 'automation.se' | 'automation.ret' | 'indstillinger.ret' | 'brugere.ret' | 'log.se'
  | 'chat.brug' | 'kort.se'
  | 'udlaeg.se' | 'udlaeg.godkend' | 'udlaeg.udbetal'
  | 'mersalg.se' | 'rapporter.se'
  | 'crm.brug' | 'crm.alle' | 'crm.godkend'
  | 'ansoegninger.se'
  | 'marketing'
  | 'analyse';

export const EVNER: { id: Evne; navn: string; gruppe: string }[] = [
  { id: 'bookinger.se', navn: 'Se bookinger, kalender og Smartplan', gruppe: 'Bookinger' },
  { id: 'bookinger.ret', navn: 'Oprette, flytte og aflyse bookinger', gruppe: 'Bookinger' },
  { id: 'bookinger.godkend', navn: 'Godkende og afvise ventende bookinger', gruppe: 'Bookinger' },
  { id: 'kunder.se', navn: 'Se kunder', gruppe: 'Kunder' },
  { id: 'kunder.ret', navn: 'Oprette og rette kunder', gruppe: 'Kunder' },
  { id: 'kunder.slet', navn: 'Slette kunder (GDPR)', gruppe: 'Kunder' },
  { id: 'kundecenter.se', navn: 'Se Kundecenter-sager', gruppe: 'Kundeservice' },
  { id: 'kundecenter.behandl', navn: 'Behandle og besvare sager', gruppe: 'Kundeservice' },
  { id: 'kundecenter.arkiv', navn: 'Se dokumentationsarkivet (alle før-, efter- og skadebilleder)', gruppe: 'Kundeservice' },
  { id: 'chatbot.se', navn: 'Se chatbottens samtaler og overtage dem', gruppe: 'Kundeservice' },
  { id: 'chatbot.ret', navn: 'Rette chatbottens vidensbase, regler og tone', gruppe: 'Kundeservice' },
  { id: 'beskeder.send', navn: 'Sende SMS og mail til kunder', gruppe: 'Kundeservice' },
  { id: 'chat.brug', navn: 'Chatte med steamerne', gruppe: 'Kundeservice' },
  { id: 'kort.se', navn: 'Se kortet med steamernes placering', gruppe: 'Kundeservice' },
  { id: 'rapporter.se', navn: 'Se Dashboard med omsætning, medlemmer og nøgletal', gruppe: 'Økonomi' },
  { id: 'analyse', navn: 'Se Analyse (dækningsbidrag, løn pr. steamer og prognose) og rette omkostningerne bag', gruppe: 'Økonomi' },
  { id: 'betalinger.se', navn: 'Se ordrer og betalinger', gruppe: 'Økonomi' },
  { id: 'udlaeg.se', navn: 'Se alle medarbejderes udlæg', gruppe: 'Økonomi' },
  { id: 'udlaeg.godkend', navn: 'Godkende og afvise udlæg (2 personer skal godkende)', gruppe: 'Økonomi' },
  { id: 'udlaeg.udbetal', navn: 'Markere udlæg som udbetalt ("Penge sendt manuelt")', gruppe: 'Økonomi' },
  { id: 'betalinger.ret', navn: 'Markere betalt og refundere', gruppe: 'Økonomi' },
  { id: 'pris.ret', navn: 'Ændre prisen på en booking manuelt', gruppe: 'Økonomi' },
  { id: 'mersalg.se', navn: 'Se ekstra salg pr. medarbejder', gruppe: 'Økonomi' },
  { id: 'medlemskaber.se', navn: 'Se medlemskaber', gruppe: 'Økonomi' },
  { id: 'medlemskaber.ret', navn: 'Oprette, pause og opsige medlemskaber', gruppe: 'Økonomi' },
  { id: 'katalog.ret', navn: 'Rette ydelser, priser, tilvalg, zoner og rabatkoder', gruppe: 'Opsætning' },
  { id: 'steamere.ret', navn: 'Rette steamere og arbejdstider', gruppe: 'Opsætning' },
  { id: 'fravaer.meld', navn: 'Melde sygdom og fri for steamere', gruppe: 'Opsætning' },
  { id: 'ansoegninger.se', navn: 'Se og behandle jobansøgninger (persondata, slettes efter 6 måneder)', gruppe: 'Opsætning' },
  { id: 'automation.se', navn: 'Se automatiseringer og sendte beskeder', gruppe: 'Opsætning' },
  { id: 'automation.ret', navn: 'Rette automatiseringer og skabeloner', gruppe: 'Opsætning' },
  { id: 'indstillinger.ret', navn: 'Rette indstillinger og formularfelter', gruppe: 'Opsætning' },
  { id: 'brugere.ret', navn: 'Invitere, rette og deaktivere brugere', gruppe: 'Opsætning' },
  { id: 'log.se', navn: 'Se aktivitetsloggen (hvem gjorde hvad)', gruppe: 'Opsætning' },
  { id: 'crm.brug', navn: 'Ringe op, markere udfald og registrere salg', gruppe: 'Salg (CRM)' },
  { id: 'crm.alle', navn: 'Se alle sælgeres leads og tal, fordele og importere leads', gruppe: 'Salg (CRM)' },
  // crm.godkend (navnet fra før 0.49.1, hvor salg skulle godkendes): ser alle salg og de solgte kunder
  { id: 'crm.godkend', navn: 'Se alle salg og de solgte kunder', gruppe: 'Salg (CRM)' },
  { id: 'marketing', navn: 'Nyhedsbreve: kampagner, automatiseringer, modtagere, rapporter og Klaviyo-import', gruppe: 'Marketing' },
];

export type KontorRolle = 'admin' | 'kundeservice' | 'saelger';
export type RolleEvner = Record<KontorRolle, Evne[]>;

const ALLE = EVNER.map((e) => e.id);
export const STANDARD_ROLLE_EVNER: RolleEvner = {
  admin: ALLE,
  kundeservice: ['bookinger.se', 'bookinger.ret', 'bookinger.godkend', 'kunder.se', 'kunder.ret', 'kundecenter.se', 'kundecenter.behandl',
    'beskeder.send', 'betalinger.se', 'medlemskaber.se', 'fravaer.meld', 'automation.se', 'chat.brug', 'kort.se', 'crm.godkend', 'chatbot.se'],
  // Sælgeren kan se kunder, historik og medlemskaber, men ikke rette dem – kun ringe, markere og registrere salg.
  saelger: ['crm.brug', 'kunder.se', 'medlemskaber.se'],
};

/** Det en sælger kan få lov til (resten kan ikke slås til). Sælgere kan aldrig rette kunder, bookinger eller historik. */
export const SAELGER_KAN: Evne[] = ['crm.brug', 'crm.alle', 'kunder.se', 'medlemskaber.se', 'chat.brug'];

export const ROLLE_NAVN: Record<Rolle, string> = {
  ejer: 'Ejer', admin: 'Admin', kundeservice: 'Kundeservice', saelger: 'Sælger', steamer: 'Steamer', kunde: 'Kunde',
};
export const ROLLE_BESKRIVELSE: Record<Exclude<Rolle, 'kunde'>, string> = {
  ejer: 'Alt. Kun ejeren kan gøre andre til admin og overdrage ejerskabet.',
  admin: 'Alt undtagen det, kun ejeren kan.',
  kundeservice: 'Bookinger, kunder, Kundecenter og beskeder.',
  saelger: 'Ringeliste, kundeopslag og historik (kun læse), markere udfald og registrere salg til godkendelse.',
  steamer: 'Egne opgaver, rute, kalender, før/efter-billeder, underskrift og chat med kontoret.',
};

/** Kun ejeren – kan ikke slås fra eller gives til andre. */
export const KUN_EJER = ['Gøre nogen til admin eller fjerne en admin', 'Overdrage ejerskabet', 'Ændre ejerens login'];

/** Rettighedstjek. Ejer kan alt. Admin kan altid alt (hans evner kan ikke indskrænkes, så ingen låser sig ude). */
export function har(rolle: Rolle | undefined | null, evne: Evne, matrix: RolleEvner = STANDARD_ROLLE_EVNER): boolean {
  if (!rolle) return false;
  if (rolle === 'ejer' || rolle === 'admin') return true;
  if (rolle === 'kundeservice') return matrix.kundeservice?.includes(evne) ?? false;
  if (rolle === 'saelger') return SAELGER_KAN.includes(evne) && (matrix.saelger ?? STANDARD_ROLLE_EVNER.saelger).includes(evne);
  return false;
}

export const erKontor = (r: Rolle | undefined | null) => r === 'ejer' || r === 'admin' || r === 'kundeservice';
/** Bruger kontorets skærme (menuen til venstre): kontoret og sælgere. */
export const brugerKontorSkal = (r: Rolle | undefined | null) => erKontor(r) || r === 'saelger';

/** Hvilke roller må denne bruger give andre? Kun ejeren kan give admin. Ingen kan give ejer (det er en overdragelse). */
export function rollerDerKanGives(min: Rolle): Exclude<Rolle, 'kunde' | 'ejer'>[] {
  if (min === 'ejer') return ['admin', 'kundeservice', 'saelger', 'steamer'];
  if (min === 'admin') return ['kundeservice', 'saelger', 'steamer'];
  return [];
}

/** Må `min` rette brugeren med rollen `deres`? Admin kan ikke rette ejeren eller andre admins. */
export function kanRetteBruger(min: Rolle, deres: Rolle): boolean {
  if (min === 'ejer') return deres !== 'ejer';
  if (min === 'admin') return deres !== 'ejer' && deres !== 'admin';
  return false;
}
